import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";

import type { ProviderType } from "@/api";
import { PROVIDER_TYPE_LABEL } from "@/lib/provider-helpers";
import { cn } from "@/lib/utils";
import { ProviderLogo } from "./provider-logo";

interface Option {
  value: string;
  label: string;
}

const AVAILABLE: ReadonlyArray<Option> = [
  { value: "openai", label: PROVIDER_TYPE_LABEL.openai },
  { value: "anthropic", label: PROVIDER_TYPE_LABEL.anthropic },
  { value: "bedrock", label: PROVIDER_TYPE_LABEL.bedrock },
];

// Display-only — not valid ProviderType yet; rendered disabled.
const COMING_SOON: ReadonlyArray<Option> = [
  { value: "google-vertex", label: "Google Vertex AI" },
  { value: "alibaba", label: "Alibaba Model Studio" },
];

interface ProviderSelectProps {
  value: ProviderType;
  onChange: (value: ProviderType) => void;
  disabled?: boolean;
  comingSoonLabel: string;
  id?: string;
}

export function ProviderSelect({
  value,
  onChange,
  disabled = false,
  comingSoonLabel,
  id,
}: ProviderSelectProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (wrapRef.current && target && !wrapRef.current.contains(target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const selected =
    AVAILABLE.find((o) => o.value === value) ?? AVAILABLE[0];

  return (
    <div ref={wrapRef} className="relative">
      <button
        id={id}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => !disabled && setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === "Escape" && open) {
            e.stopPropagation();
            setOpen(false);
          }
        }}
        className={cn(
          "flex h-10 w-full items-center gap-2 rounded-md border border-ocean-100 bg-white px-3 text-left text-sm",
          "focus:outline-none focus:ring-2 focus:ring-ocean-400",
          "disabled:cursor-not-allowed disabled:bg-ocean-50/50"
        )}
      >
        <ProviderLogo provider={selected.value} />
        <span className="flex-1 truncate text-ocean-900">{selected.label}</span>
        <ChevronDown
          size={16}
          className={cn(
            "shrink-0 text-ocean-400 transition-transform",
            open && "rotate-180"
          )}
        />
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute z-10 mt-1 w-full overflow-hidden rounded-md border border-ocean-100 bg-white py-1 shadow-lg"
        >
          {AVAILABLE.map((opt) => {
            const isActive = opt.value === value;
            return (
              <li key={opt.value}>
                <button
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  onClick={() => {
                    onChange(opt.value as ProviderType);
                    setOpen(false);
                  }}
                  className={cn(
                    "flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors",
                    isActive
                      ? "bg-ocean-50 text-ocean-900"
                      : "text-ocean-800 hover:bg-ocean-50/70"
                  )}
                >
                  <ProviderLogo provider={opt.value} />
                  <span className="flex-1 truncate">{opt.label}</span>
                  {isActive && (
                    <Check size={15} className="shrink-0 text-ocean-500" />
                  )}
                </button>
              </li>
            );
          })}

          <li role="presentation" className="my-1 border-t border-ocean-100" />

          {COMING_SOON.map((opt) => (
            <li key={opt.value}>
              <div
                role="option"
                aria-disabled="true"
                aria-selected="false"
                className="flex w-full cursor-not-allowed items-center gap-2.5 px-3 py-2 text-sm opacity-60"
              >
                <ProviderLogo provider={opt.value} className="grayscale" />
                <span className="flex-1 truncate text-neutral-500">
                  {opt.label}
                </span>
                <span className="shrink-0 rounded-full bg-ocean-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-ocean-500">
                  {comingSoonLabel}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
